// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * CargoContext - Manage trade goods inventory
 * 
 * Handles buying, selling, and storing trade goods.
 * Cargo capacity determined by installed modules.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';

const CargoContext = createContext(null);

export function CargoProvider({ children }) {
  // Cargo inventory: { goodId: quantity }
  const [cargo, setCargo] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CARGO);
    return saved ? JSON.parse(saved) : {};
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CARGO, JSON.stringify(cargo));
  }, [cargo]);

  // Get total cargo units used
  const getCargoUsed = useCallback((tradeGoods) => {
    return Object.entries(cargo).reduce((total, [goodId, quantity]) => {
      const good = tradeGoods[goodId];
      if (!good) return total;
      return total + (quantity * good.weight);
    }, 0);
  }, [cargo]);

  // Get cargo space remaining
  const getCargoRemaining = useCallback((maxCapacity, tradeGoods) => {
    return maxCapacity - getCargoUsed(tradeGoods);
  }, [getCargoUsed]);

  // Check if can add cargo
  const canAddCargo = useCallback((goodId, quantity, maxCapacity, tradeGoods) => {
    const good = tradeGoods[goodId];
    if (!good) return false;
    
    const spaceNeeded = quantity * good.weight;
    const spaceAvailable = getCargoRemaining(maxCapacity, tradeGoods);
    
    return spaceNeeded <= spaceAvailable;
  }, [getCargoRemaining]);

  // Add cargo
  const addCargo = useCallback((goodId, quantity) => {
    setCargo(prev => ({
      ...prev,
      [goodId]: (prev[goodId] || 0) + quantity
    }));
  }, []);

  // Remove cargo
  const removeCargo = useCallback((goodId, quantity) => {
    setCargo(prev => {
      const newQuantity = (prev[goodId] || 0) - quantity;
      if (newQuantity <= 0) {
        const { [goodId]: removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [goodId]: newQuantity
      };
    });
  }, []);

  // Get quantity of specific good
  const getCargoQuantity = useCallback((goodId) => {
    return cargo[goodId] || 0;
  }, [cargo]);

  // Get all cargo as array
  const getCargoList = useCallback(() => {
    return Object.entries(cargo).map(([goodId, quantity]) => ({
      goodId,
      quantity
    }));
  }, [cargo]);

  // Clear all cargo
  const clearCargo = useCallback(() => {
    setCargo({});
  }, []);

  // Reset (for game reset)
  const resetCargo = useCallback(() => {
    setCargo({});
    localStorage.removeItem(STORAGE_KEYS.CARGO);
  }, []);

  const value = {
    cargo,
    getCargoUsed,
    getCargoRemaining,
    canAddCargo,
    addCargo,
    removeCargo,
    getCargoQuantity,
    getCargoList,
    clearCargo,
    resetCargo
  };

  return (
    <CargoContext.Provider value={value}>
      {children}
    </CargoContext.Provider>
  );
}

export function useCargo() {
  const context = useContext(CargoContext);
  if (!context) {
    throw new Error('useCargo must be used within a CargoProvider');
  }
  return context;
}

export default CargoContext;
